/**
 * Sprint P32 · Cockpit Inspector — colormaps, cut-off, slicer, filters,
 * vertical exaggeration, colour-by mode selectors.
 */
import { COLORMAPS } from "../utils/colorPalettes";


export function InspectorPanel({
  palette, setPalette,
  cutoff, setCutoff, cutoffMin, cutoffMax, unit,
  reportingCutoff, cutoffMode, setCutoffMode,
  slicer, setSlicer, slicerPos, setSlicerPos, slicerRange,
  colourBy, setColourBy, minSamples, setMinSamples,
  holeColourBy, setHoleColourBy,
  classFilter, setClassFilter, zSlab, setZSlab, zRange,
  vExag, setVExag, vExagMode, setVExagMode,
}) {
  const toggleClass = (c) => {
    const next = new Set(classFilter);
    if (next.has(c)) next.delete(c); else next.add(c);
    setClassFilter(next);
  };
  const CLASSES = [
    { key: "Measured",     tone: "text-emerald-700 bg-emerald-100 border-emerald-300" },
    { key: "Indicated",    tone: "text-blue-700 bg-blue-100 border-blue-300" },
    { key: "Inferred",     tone: "text-amber-700 bg-amber-100 border-amber-300" },
    { key: "Unclassified", tone: "text-slate-700 bg-slate-100 border-slate-300" },
  ];
  const isVisualOverride = reportingCutoff != null &&
      Math.abs(cutoff - reportingCutoff) > 1e-6;

  return (
    <div className="w-56 shrink-0 bg-slate-950 border-l border-slate-800 flex flex-col overflow-y-auto text-slate-200"
         data-testid="cockpit-inspector">
      <div className="px-3 py-2 border-b border-slate-800">
        <div className="text-[9px] font-mono uppercase tracking-widest text-sky-400">
          Colormap
        </div>
      </div>
      <div className="p-2 space-y-1.5">
        {Object.entries(COLORMAPS).map(([name, colours]) => (
          <button key={name} onClick={() => setPalette(name)}
                  data-testid={`cockpit-palette-${name}`}
                  className={`w-full p-1.5 rounded border transition text-left flex items-center gap-2 ${
                    palette === name
                      ? "border-sky-500 bg-sky-500/10"
                      : "border-slate-800 hover:border-slate-700 bg-slate-900"
                  }`}>
            <div className="flex-1 h-3 rounded overflow-hidden flex">
              {colours.map((c, i) => (
                <div key={i} className="flex-1" style={{ backgroundColor: c }} />
              ))}
            </div>
            <div className="text-[10px] font-mono text-slate-300 capitalize w-14">{name}</div>
          </button>
        ))}
      </div>

      {/* Cut-off */}
      <div className="px-3 py-2 border-t border-slate-800">
        <div className="flex items-center justify-between mb-1">
          <div className="text-[9px] font-mono uppercase tracking-widest text-slate-400">
            Cutoff — {unit}
          </div>
          <div className="flex text-[9px]">
            <button onClick={() => setCutoffMode("auto")}
                    data-testid="cockpit-cutoff-mode-auto"
                    className={`px-1.5 py-0.5 rounded-l border ${
                      cutoffMode === "auto"
                        ? "bg-brand-blue/10 text-sky-400 border-brand-blue"
                        : "border-slate-800 text-slate-400"
                    }`}>Auto</button>
            <button onClick={() => setCutoffMode("manual")}
                    data-testid="cockpit-cutoff-mode-manual"
                    className={`px-1.5 py-0.5 rounded-r border-t border-b border-r ${
                      cutoffMode === "manual"
                        ? "bg-brand-blue/10 text-sky-400 border-brand-blue"
                        : "border-slate-800 text-slate-400"
                    }`}>Manual</button>
          </div>
        </div>
        <input type="range"
               min={cutoffMin} max={cutoffMax}
               step={(cutoffMax - cutoffMin) / 100 || 0.01}
               value={cutoff}
               disabled={cutoffMode === "auto"}
               onChange={(e) => setCutoff(Number(e.target.value))}
               data-testid="cockpit-cutoff-slider"
               className="w-full" />
        {cutoffMode === "manual" && (
          <input type="number" step={0.001} value={cutoff}
                 onChange={(e) => setCutoff(Number(e.target.value))}
                 data-testid="cockpit-cutoff-input"
                 className="w-full mt-1 px-1 py-0.5 border border-slate-800 rounded text-[10px] font-mono" />
        )}
        <div className="text-[10px] font-mono text-slate-100 flex items-center justify-between mt-1">
          <span>Visual: <b>{cutoff.toFixed(3)}</b> {unit}</span>
          {reportingCutoff != null && (
            <span className="text-slate-400">Report: {reportingCutoff.toFixed(3)} {unit}</span>
          )}
        </div>
        {isVisualOverride && (
          <div className="mt-1 text-[9px] text-amber-300 bg-amber-900/20 border border-amber-700/50 rounded px-1.5 py-1"
               data-testid="cockpit-cutoff-visual-warning">
            Viewer sensitivity only — official reporting cut-off unchanged.
            <button onClick={() => setCutoff(reportingCutoff)}
                    data-testid="cockpit-cutoff-reset"
                    className="ml-1 underline text-sky-400">Reset to reporting</button>
          </div>
        )}
      </div>

      {/* Section slicer */}
      <div className="px-3 py-2 border-t border-slate-800">
        <div className="text-[9px] font-mono uppercase tracking-widest text-slate-400 mb-1">
          Section slicer
        </div>
        <div className="grid grid-cols-3 gap-1 mb-2">
          {[["off","OFF"],["ns","N-S"],["ew","E-W"]].map(([v, l]) => (
            <button key={v}
                    onClick={() => setSlicer(v)}
                    data-testid={`cockpit-slicer-${v}`}
                    className={`text-[10px] font-mono py-1 rounded border transition ${
                      slicer === v
                        ? "border-sky-500 bg-sky-500/10 text-sky-300"
                        : "border-slate-800 text-slate-300 hover:border-slate-700"
                    }`}>{l}</button>
          ))}
        </div>
        {slicer !== "off" && slicerRange && (
          <>
            <input type="range"
                   min={slicerRange[0]} max={slicerRange[1]}
                   step={(slicerRange[1] - slicerRange[0]) / 100 || 1}
                   value={slicerPos}
                   onChange={(e) => setSlicerPos(Number(e.target.value))}
                   data-testid="cockpit-slicer-slider"
                   className="w-full" />
            <div className="text-[10px] font-mono text-slate-100 text-right mt-1">
              {slicer === "ns" ? "X" : "Y"} = {slicerPos.toFixed(0)} m
            </div>
          </>
        )}
      </div>

      {/* Vertical exaggeration */}
      <div className="px-3 py-2 border-t border-slate-800">
        <div className="flex items-center justify-between mb-1">
          <div className="text-[9px] font-mono uppercase tracking-widest text-slate-400">
            Vertical exaggeration
          </div>
          <div className="flex text-[9px]">
            <button onClick={() => setVExagMode("preset")}
                    data-testid="cockpit-vexag-mode-preset"
                    className={`px-1.5 py-0.5 rounded-l border ${
                      vExagMode === "preset"
                        ? "bg-brand-blue/10 text-sky-400 border-brand-blue"
                        : "border-slate-800 text-slate-400"
                    }`}>Fixed</button>
            <button onClick={() => setVExagMode("manual")}
                    data-testid="cockpit-vexag-mode-manual"
                    className={`px-1.5 py-0.5 rounded-r border-t border-b border-r ${
                      vExagMode === "manual"
                        ? "bg-brand-blue/10 text-sky-400 border-brand-blue"
                        : "border-slate-800 text-slate-400"
                    }`}>Manual</button>
          </div>
        </div>
        {vExagMode === "preset" ? (
          <div className="grid grid-cols-4 gap-1">
            {[1, 2, 3, 5].map((v) => (
              <button key={v} onClick={() => setVExag(v)}
                      data-testid={`cockpit-vexag-${v}x`}
                      className={`text-[10px] py-1 rounded border font-mono ${
                        vExag === v
                          ? "border-sky-500 bg-sky-500/10 text-sky-300"
                          : "border-slate-800 text-slate-300"
                      }`}>{v}×</button>
            ))}
          </div>
        ) : (
          <input type="number" step={0.5} min={0.1} max={20} value={vExag}
                 onChange={(e) => setVExag(Number(e.target.value) || 1)}
                 data-testid="cockpit-vexag-input"
                 className="w-full px-1 py-0.5 border border-slate-800 rounded text-[10px] font-mono" />
        )}
      </div>

      {/* Colour-by */}
      <div className="px-3 py-2 border-t border-slate-800">
        <div className="text-[9px] font-mono uppercase tracking-widest text-slate-400 mb-1">
          Block colour by
        </div>
        <div className="grid grid-cols-2 gap-1">
          {[
            ["grade",          "Grade"],
            ["classification", "Class"],
            ["lithology",      "Litho"],
            ["ore_waste",      "Ore/Waste"],
            ["pass",           "Est. pass"],
            ["domain",         "Domain"],
          ].map(([v, l]) => (
            <button key={v} onClick={() => setColourBy(v)}
                    data-testid={`cockpit-colourby-${v}`}
                    className={`text-[10px] font-mono py-1 rounded border ${
                      colourBy === v
                        ? "border-sky-500 bg-sky-500/10 text-sky-300"
                        : "border-slate-800 text-slate-300"
                    }`}>{l}</button>
          ))}
        </div>
      </div>

      {/* Drillhole colour-by (Sprint P29 · 3D Drillhole Cylinders) */}
      <div className="px-3 py-2 border-t border-slate-800">
        <div className="text-[9px] font-mono uppercase tracking-widest text-slate-400 mb-1">
          Drillhole colour by
        </div>
        <div className="grid grid-cols-2 gap-1">
          {[
            ["lithology",      "Litho"],
            ["grade",          "Grade"],
            ["classification", "Class"],
            ["ore_waste",      "Ore/Waste"],
          ].map(([v, l]) => (
            <button key={v} onClick={() => setHoleColourBy(v)}
                    data-testid={`cockpit-hole-colourby-${v}`}
                    className={`text-[10px] font-mono py-1 rounded border ${
                      holeColourBy === v
                        ? "border-amber-500 bg-amber-500/10 text-amber-300"
                        : "border-slate-800 text-slate-300"
                    }`}>{l}</button>
          ))}
        </div>
      </div>

      {/* Classification filter */}
      <div className="px-3 py-2 border-t border-slate-800">
        <div className="text-[9px] font-mono uppercase tracking-widest text-slate-400 mb-1">
          Classification filter
        </div>
        <div className="text-[10px] text-slate-400 mb-1">
          {classFilter.size === 0 ? "showing all" : `showing ${classFilter.size}/4`}
        </div>
        <div className="grid grid-cols-2 gap-1">
          {CLASSES.map(({ key, tone }) => (
            <button key={key} onClick={() => toggleClass(key)}
                    data-testid={`cockpit-class-${key.toLowerCase()}`}
                    className={`text-[9px] font-mono py-1 rounded border ${
                      classFilter.has(key) || classFilter.size === 0
                        ? tone
                        : "border-slate-800 text-slate-500 line-through"
                    }`}>{key}</button>
          ))}
        </div>
      </div>

      {/* Min-samples floor */}
      <div className="px-3 py-2 border-t border-slate-800">
        <div className="text-[9px] font-mono uppercase tracking-widest text-slate-400 mb-1">
          Min samples per block
        </div>
        <input type="range" min={0} max={20} step={1} value={minSamples}
               onChange={(e) => setMinSamples(Number(e.target.value))}
               data-testid="cockpit-min-samples"
               className="w-full" />
        <div className="text-[10px] font-mono text-slate-100 text-right mt-1">
          ≥ {minSamples} samples
        </div>
      </div>

      {/* Elevation slab */}
      {zRange && (
        <div className="px-3 py-2 border-t border-slate-800">
          <div className="text-[9px] font-mono uppercase tracking-widest text-slate-400 mb-1">
            Elevation slab (m)
          </div>
          <div className="flex items-center gap-1 text-[10px] font-mono text-slate-100">
            <input type="number" step="10"
                   value={zSlab?.[0] ?? zRange[0]}
                   onChange={(e) => setZSlab([Number(e.target.value), zSlab?.[1] ?? zRange[1]])}
                   className="w-full bg-slate-900 border border-slate-700 rounded px-1 py-0.5 text-slate-100"
                   data-testid="cockpit-z-min" />
            <span>–</span>
            <input type="number" step="10"
                   value={zSlab?.[1] ?? zRange[1]}
                   onChange={(e) => setZSlab([zSlab?.[0] ?? zRange[0], Number(e.target.value)])}
                   className="w-full bg-slate-900 border border-slate-700 rounded px-1 py-0.5 text-slate-100"
                   data-testid="cockpit-z-max" />
          </div>
          <button onClick={() => setZSlab(null)}
                  className="text-[10px] mt-1 text-sky-400 underline"
                  data-testid="cockpit-z-reset">
            Reset slab
          </button>
        </div>
      )}
    </div>
  );
}
